/**
 * WebsiteInfoModal - Shows opt-out information for a website
 *
 * Displays extended website configuration data from Gobodo:
 * - Opt-Out URL (clickable link)
 * - Opt-Out Email(s)
 * - Requirements
 * - Notes
 * - PII Types (badges)
 *
 * Only shows sections that have data. Shows "No additional information"
 * if no extended data is available.
 */

import React from 'react';
import { X, ExternalLink, Mail, FileText, AlertCircle, Shield, Loader2 } from 'lucide-react';
import { useWebsiteConfigurationByName } from '../../services/queries/cyberSweepQueries';
import { PII_TYPES, PIIType } from '../../data/websiteConfigurations';

interface WebsiteInfoModalProps {
  websiteName: string;
  onClose: () => void;
}

const WebsiteInfoModal: React.FC<WebsiteInfoModalProps> = ({ websiteName, onClose }) => {
  // Fetch website configuration by name
  const { data: websiteConfig, isLoading, error } = useWebsiteConfigurationByName(websiteName);

  console.log('[WebsiteInfoModal] Render state:', {
    websiteName,
    isLoading,
    error: error?.message || error,
    websiteConfig: websiteConfig ? {
      name: websiteConfig.name,
      optOutUrl: websiteConfig.optOutUrl,
      optOutEmail: websiteConfig.optOutEmail,
      hasRequirements: !!websiteConfig.requirements,
      hasNotes: !!websiteConfig.notes,
      piiTypesCount: websiteConfig.piiTypes?.length ?? 0,
    } : null,
  });

  // Check if we have any extended data
  const hasExtendedData = websiteConfig && (
    websiteConfig.optOutUrl ||
    websiteConfig.optOutEmail ||
    websiteConfig.requirements ||
    websiteConfig.notes ||
    (websiteConfig.piiTypes && websiteConfig.piiTypes.length > 0)
  );

  // Get PII type label from value
  const getPiiLabel = (piiValue: PIIType): string => {
    const found = PII_TYPES.find(p => p.value === piiValue);
    return found ? found.label : piiValue;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {websiteName}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Loading website information...</span>
            </div>
          ) : error ? (
            <div className="py-4">
              <div className="flex items-center gap-2 text-red-500 mb-2">
                <AlertCircle className="h-5 w-5" />
                <span>Error loading website information</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {error instanceof Error ? error.message : String(error)}
              </p>
            </div>
          ) : !hasExtendedData ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No additional information available for this website.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Opt-Out URL */}
              {websiteConfig.optOutUrl && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Opt-Out URL
                  </label>
                  <a
                    href={websiteConfig.optOutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {websiteConfig.optOutUrl}
                  </a>
                </div>
              )}

              {/* Opt-Out Email(s) */}
              {websiteConfig.optOutEmail && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    <Mail className="h-3.5 w-3.5" />
                    Opt-Out Email(s)
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {websiteConfig.optOutEmail}
                  </p>
                </div>
              )}

              {/* Requirements */}
              {websiteConfig.requirements && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    <FileText className="h-3.5 w-3.5" />
                    Requirements
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap text-sm bg-gray-50 dark:bg-gray-700/50 p-3 rounded border border-gray-200 dark:border-gray-600">
                    {websiteConfig.requirements}
                  </p>
                </div>
              )}

              {/* Notes */}
              {websiteConfig.notes && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    <FileText className="h-3.5 w-3.5" />
                    Notes
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap text-sm bg-gray-50 dark:bg-gray-700/50 p-3 rounded border border-gray-200 dark:border-gray-600">
                    {websiteConfig.notes}
                  </p>
                </div>
              )}

              {/* PII Types */}
              {websiteConfig.piiTypes && websiteConfig.piiTypes.length > 0 && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    <Shield className="h-3.5 w-3.5" />
                    PII Types Collected
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {websiteConfig.piiTypes.map((piiType) => (
                      <span
                        key={piiType}
                        className="px-2.5 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full"
                      >
                        {getPiiLabel(piiType)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteInfoModal;
